import React, { FC, useState } from 'react';
import {
  Backdrop,
  Button,
  Card,
  CardActions,
  CardContent,
  Fab,
  Grid,
  Modal,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core';
import useNotifications from '../../../Notifications/useNotifications';
import AddIcon from '@material-ui/icons/Add';
import { useGetPartnersQuery } from './graphql/getPartners.generated';
import { useCreatePartnerMutation } from './graphql/createPartner.generated';
import { useDeletePartnerMutation } from './graphql/deletePartner.generated';

interface Props {}

const useStyles = makeStyles((theme) => ({
  fab: {
    position: 'fixed',
    bottom: '2rem',
    right: '2rem'
  },
  modal: {
    display: 'flex',
    paddingTop: '10rem',
    justifyContent: 'center'
  },
  modalPaper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    height: '25%',
    width: '50%',
    padding: theme.spacing(2, 4, 3)
  }
}));

const Partners: FC<Props> = () => {
  const { fab, modal, modalPaper } = useStyles();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [textInput, setTextInput] = useState<string>('');
  const { data, refetch } = useGetPartnersQuery();
  const partners = data?.getPartners || [];
  const { addError, addSuccess } = useNotifications();
  const [deletePartner] = useDeletePartnerMutation({
    onError: () => addError('There was an error deleting this partner.'),
    onCompleted: (completedData) => {
      if (completedData && completedData.deletePartner) {
        addSuccess('Partner deleted.');
        refetch();
      } else {
        addError('There was an error deleting this partner.');
      }
    }
  });

  const [createRoomType] = useCreatePartnerMutation({
    onError: () => addError('There was an error creating this partner.'),
    onCompleted: (completedData) => {
      console.log({ completedData });
      if (completedData && completedData.createPartner) {
        addSuccess('Partner created successfully.');
        setIsModalOpen(false);
        refetch();
      } else {
        addError('There was an error creating this partner.');
      }
    }
  });

  const handleDeleteRoomType = async (value: string) => {
    await deletePartner({ variables: { id: value } });
  };

  const handleCreateRoomType = async (value: string) => {
    await createRoomType({ variables: { input: { name: value } } });
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setTextInput('');
  };

  return (
    <>
      <h1 className="my-6 text-3xl">Partners</h1>
      <Fab color="primary" onClick={() => setIsModalOpen(true)} aria-label="add" className={fab}>
        <AddIcon />
      </Fab>
      <Grid container spacing={2}>
        {partners.map((partner) => (
          <Grid item xs={4}>
            <Card>
              <CardContent>
                <Typography gutterBottom variant="h5" component="div">
                  {partner.name}
                </Typography>
              </CardContent>
              <CardActions>
                <Button onClick={() => handleDeleteRoomType(partner.id)} size="small">
                  Delete
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Modal
        className={modal}
        open={isModalOpen}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <div className={modalPaper}>
          <h2>New partner</h2>
          <TextField
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            style={{ marginBottom: '1rem', width: '100%' }}
          />
          <div>
            <Button
              color="primary"
              disabled={!textInput.length}
              onClick={() => handleCreateRoomType(textInput)}
              variant="contained"
            >
              Create
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Partners;
